<!-- System Configuration -->
<div class="page-header">
  <div class="header-content">
    <div class="header-main">
      <div class="header-icon">
        <i class="fas fa-cog"></i>
      </div>
      <div class="header-text">
        <h1 class="page-title">System Configuration</h1>
        <p class="page-description">Configure business settings, branding, and system preferences</p>
      </div>
    </div>
    <div class="header-actions">
      <button class="btn btn-outline-secondary" (click)="loadBusinessDetails()">
        <i class="fas fa-sync-alt me-1"></i>Refresh
      </button>
      <button class="btn btn-primary" (click)="saveAllSettings()" [disabled]="isSaving">
        <span *ngIf="!isSaving">
          <i class="fas fa-save me-1"></i>Save All Changes
        </span>
        <span *ngIf="isSaving">
          <i class="fas fa-spinner fa-spin me-1"></i>Saving...
        </span>
      </button>
    </div>
  </div>
</div>

<!-- Success/Error Messages -->
@if (successMessage) {
  <div style="margin-bottom: 20px;">
    <ngb-alert #selfClosingAlert [type]="type" (closed)="successMessage = ''">
      <div style="display: flex; align-items: center; gap: 8px;">
        <i class="fas fa-check-circle" *ngIf="type === 'success'"></i>
        <i class="fas fa-exclamation-triangle" *ngIf="type === 'warning'"></i>
        <i class="fas fa-times-circle" *ngIf="type === 'danger'"></i>
        {{ message }}
      </div>
    </ngb-alert>
  </div>
}

<div class="row g-4">
  <!-- Main Content -->
  <div class="col-lg-8">
    <!-- Business Branding Panel -->
    <div class="data-panel" style="margin-bottom: 20px;">
      <div class="panel-header">
        <div class="panel-title">
          <div class="title-icon">
            <i class="fas fa-building"></i>
          </div>
          <div class="title-content">
            <div class="title-text">Business Branding</div>
            <div class="title-subtitle">Configure your business identity and branding</div>
          </div>
        </div>
      </div>

      <div class="panel-body" style="padding: 24px;">
        @if(isLoadingBusinessDetails) {
          <div class="loading-state">
            <div class="loading-content">
              <div class="loading-spinner">
                <div class="spinner-ring"></div>
              </div>
              <div class="loading-text">
                <h5>Loading Business Details</h5>
                <p>Please wait while we fetch your business information...</p>
              </div>
            </div>
          </div>
        } @else {
          <div class="row g-3">
            <!-- Business Name -->
            <div class="col-12">
              <div class="form-group">
                <label class="form-label">
                  <i class="fas fa-store me-1"></i>
                  Business Name <span style="color: var(--danger-text);">*</span>
                </label>
                <input type="text"
                       class="form-control"
                       placeholder="Enter your business name"
                       [(ngModel)]="name">
                <div style="font-size: 12px; color: var(--text-tertiary); margin-top: 4px;">This will appear on invoices and receipts</div>
              </div>
            </div>

            <!-- Revenue Head -->
            <div class="col-12">
              <div class="form-group">
                <label class="form-label">
                  <i class="fas fa-user-tie me-1"></i>
                  Revenue Head
                </label>
                <input type="text"
                       class="form-control"
                       placeholder="Enter revenue head name"
                       [(ngModel)]="revenue_head">
                <div style="font-size: 12px; color: var(--text-tertiary); margin-top: 4px;">Person responsible for revenue operations</div>
              </div>
            </div>

            <!-- Keys Row -->
            <div class="col-md-6">
              <div class="form-group">
                <label class="form-label">
                  <i class="fas fa-key me-1"></i>
                  Secret Key
                </label>
                <input type="password"
                       class="form-control"
                       placeholder="Enter secret key"
                       [(ngModel)]="secret_key">
                <div style="font-size: 12px; color: var(--text-tertiary); margin-top: 4px;">Keep this secure and private</div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label class="form-label">
                  <i class="fas fa-unlock me-1"></i>
                  Public Key
                </label>
                <input type="text"
                       class="form-control"
                       placeholder="Enter public key"
                       [(ngModel)]="public_key">
                <div style="font-size: 12px; color: var(--text-tertiary); margin-top: 4px;">Used for API authentication</div>
              </div>
            </div>

            <!-- Logo Upload -->
            <div class="col-12">
              <div class="form-group">
                <label class="form-label">
                  <i class="fas fa-image me-1"></i>
                  Business Logo
                </label>

                @if(uploadProgress()){
                  <div style="margin-bottom: 12px;">
                    <ngb-progressbar [value]="uploadProgress()" type="primary" />
                    <div style="font-size: 12px; color: var(--text-tertiary); margin-top: 4px; text-align: center;">Uploading... {{ uploadProgress() }}%</div>
                  </div>
                }

                <div style="display: flex; gap: 20px; align-items: flex-start; padding: 20px; border: 2px dashed var(--border); border-radius: var(--radius-lg); background: var(--bg-muted); transition: all var(--transition);"
                     [class.success]="uploadSuccess"
                     [class.error]="uploadError"
                     (drop)="onFileDrop($event)"
                     (dragover)="onDragOver($event)">

                  <!-- Preview -->
                  <div style="width: 120px; height: 120px; border-radius: var(--radius-md); overflow: hidden; border: 1px solid var(--border); background: var(--bg-surface); display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
                    @if(imagePreview() || main_image !== null){
                      <img [src]="imagePreview() || main_image"
                           alt="Business Logo"
                           style="width: 100%; height: 100%; object-fit: cover;" />
                    }@else{
                      <div style="text-align: center; color: var(--text-tertiary);">
                        <i class="fas fa-cloud-upload-alt fa-2x" style="margin-bottom: 8px;"></i>
                        <div style="font-size: 11px;">Drop logo here</div>
                      </div>
                    }
                  </div>

                  <!-- Upload Info -->
                  <div style="flex: 1;">
                    @if(imageName()){
                      <div>
                        <div style="font-size: 14px; font-weight: 600; color: var(--text-primary); margin-bottom: 2px;">{{ imageName() }}</div>
                        <div style="font-size: 12px; color: var(--text-tertiary); margin-bottom: 12px;">{{ fileSize() }} KB</div>
                        <div style="display: flex; gap: 8px; flex-wrap: wrap;">
                          <button class="btn btn-sm btn-outline-secondary" (click)="fileInput.click()">
                            <i class="fas fa-edit me-1"></i>Change Logo
                          </button>
                          @if(selectedFile || imagePreview() !== null){
                            <button class="btn btn-sm btn-danger" (click)="removeImage()">
                              <i class="fas fa-trash me-1"></i>Remove
                            </button>
                          }
                        </div>
                      </div>
                    }@else{
                      <div>
                        <div style="font-size: 14px; font-weight: 600; color: var(--text-primary); margin-bottom: 4px;">Upload Business Logo</div>
                        <div style="font-size: 13px; color: var(--text-secondary); margin-bottom: 8px;">Drag and drop your logo or click to select a file</div>
                        <div style="display: flex; gap: 12px; margin-bottom: 12px;">
                          <span class="badge badge-light">PNG, JPG up to 5MB</span>
                          <span class="badge badge-light">Recommended: 200x200px</span>
                        </div>
                        <button class="btn btn-sm btn-primary" (click)="fileInput.click()">
                          <i class="fas fa-upload me-1"></i>Select Logo
                        </button>
                      </div>
                    }
                  </div>

                  <input type="file"
                         accept=".jpg, .jpeg, .png"
                         (change)="onFileChange($event)"
                         hidden
                         #fileInput />
                </div>
              </div>
            </div>
          </div>

          <div style="padding-top: 20px; border-top: 1px solid var(--border); margin-top: 20px; display: flex; justify-content: flex-end;">
            <button class="btn btn-primary" (click)="saveBusinessDetails()" [disabled]="isSavingBranding || isLoadingBusinessDetails">
              <span *ngIf="!isSavingBranding">
                <i class="fas fa-save me-1"></i>Save Branding
              </span>
              <span *ngIf="isSavingBranding">
                <i class="fas fa-spinner fa-spin me-1"></i>Saving...
              </span>
            </button>
          </div>
        }
      </div>
    </div>

    <!-- Financial Deductions Panel -->
    <div class="data-panel">
      <div class="panel-header">
        <div class="panel-title">
          <div class="title-icon">
            <i class="fas fa-calculator"></i>
          </div>
          <div class="title-content">
            <div class="title-text">Financial Deductions</div>
            <div class="title-subtitle">Configure taxes and service charges</div>
          </div>
        </div>
      </div>

      <div class="panel-body" style="padding: 24px;">
        @if(isLoadingDeductions) {
          <div class="loading-state">
            <div class="loading-content">
              <div class="loading-spinner">
                <div class="spinner-ring"></div>
              </div>
              <div class="loading-text">
                <h5>Loading Deduction Settings</h5>
                <p>Please wait while we fetch your tax and service charge configurations...</p>
              </div>
            </div>
          </div>
        } @else {
          <!-- VAT Section -->
          <div style="padding: 20px; border: 1px solid var(--border); border-radius: var(--radius-md); margin-bottom: 16px; background: var(--bg-muted);">
            <form [formGroup]="vatForm">
              <div style="margin-bottom: 16px;">
                <div style="font-size: 14px; font-weight: 600; color: var(--text-primary); margin-bottom: 2px;">
                  <i class="fas fa-percentage me-2" style="color: var(--accent);"></i>Value Added Tax (VAT)
                </div>
                <div style="font-size: 12px; color: var(--text-tertiary);">Configure VAT rate for your business</div>
              </div>

              <div class="row g-3 align-items-end">
                <div class="col-md-5">
                  <div style="position: relative;">
                    <input type="number"
                           class="form-control"
                           formControlName="value"
                           placeholder="0.00">
                    <span style="position: absolute; right: 14px; top: 50%; transform: translateY(-50%); font-size: 13px; color: var(--text-tertiary); font-weight: 600;">
                      <span *ngIf="vatForm.get('type')?.value === 'percent'">%</span>
                      <span *ngIf="vatForm.get('type')?.value === 'fixed'">&#8358;</span>
                    </span>
                  </div>
                </div>
                <div class="col-md-7">
                  <div class="btn-group">
                    <button type="button"
                            class="btn"
                            [class.btn-primary]="vatForm.get('type')?.value === 'percent'"
                            [class.btn-outline-secondary]="vatForm.get('type')?.value !== 'percent'"
                            (click)="vatForm.patchValue({type: 'percent'})">
                      <i class="fas fa-percentage me-1"></i>Percentage
                    </button>
                    <button type="button"
                            class="btn"
                            [class.btn-primary]="vatForm.get('type')?.value === 'fixed'"
                            [class.btn-outline-secondary]="vatForm.get('type')?.value !== 'fixed'"
                            (click)="vatForm.patchValue({type: 'fixed'})">
                      <i class="fas fa-coins me-1"></i>Fixed Amount
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>

          <!-- Withholding Tax Section -->
          <div style="padding: 20px; border: 1px solid var(--border); border-radius: var(--radius-md); margin-bottom: 16px; background: var(--bg-muted);">
            <form [formGroup]="taxForm">
              <div style="margin-bottom: 16px;">
                <div style="font-size: 14px; font-weight: 600; color: var(--text-primary); margin-bottom: 2px;">
                  <i class="fas fa-file-invoice-dollar me-2" style="color: var(--accent);"></i>Withholding Tax
                </div>
                <div style="font-size: 12px; color: var(--text-tertiary);">Configure withholding tax rate</div>
              </div>

              <div class="row g-3 align-items-end">
                <div class="col-md-5">
                  <div style="position: relative;">
                    <input type="number"
                           class="form-control"
                           formControlName="value"
                           placeholder="0.00">
                    <span style="position: absolute; right: 14px; top: 50%; transform: translateY(-50%); font-size: 13px; color: var(--text-tertiary); font-weight: 600;">
                      <span *ngIf="taxForm.get('type')?.value === 'percent'">%</span>
                      <span *ngIf="taxForm.get('type')?.value === 'fixed'">&#8358;</span>
                    </span>
                  </div>
                </div>
                <div class="col-md-7">
                  <div class="btn-group">
                    <button type="button"
                            class="btn"
                            [class.btn-primary]="taxForm.get('type')?.value === 'percent'"
                            [class.btn-outline-secondary]="taxForm.get('type')?.value !== 'percent'"
                            (click)="taxForm.patchValue({type: 'percent'})">
                      <i class="fas fa-percentage me-1"></i>Percentage
                    </button>
                    <button type="button"
                            class="btn"
                            [class.btn-primary]="taxForm.get('type')?.value === 'fixed'"
                            [class.btn-outline-secondary]="taxForm.get('type')?.value !== 'fixed'"
                            (click)="taxForm.patchValue({type: 'fixed'})">
                      <i class="fas fa-coins me-1"></i>Fixed Amount
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>

          <!-- Service Charge Section -->
          <div style="padding: 20px; border: 1px solid var(--border); border-radius: var(--radius-md); background: var(--bg-muted);">
            <form [formGroup]="servicechargeForm">
              <div style="margin-bottom: 16px;">
                <div style="font-size: 14px; font-weight: 600; color: var(--text-primary); margin-bottom: 2px;">
                  <i class="fas fa-concierge-bell me-2" style="color: var(--accent);"></i>Service Charge
                </div>
                <div style="font-size: 12px; color: var(--text-tertiary);">Configure service charge for orders</div>
              </div>

              <div class="row g-3 align-items-end">
                <div class="col-md-5">
                  <div style="position: relative;">
                    <input type="number"
                           class="form-control"
                           formControlName="value"
                           placeholder="0.00">
                    <span style="position: absolute; right: 14px; top: 50%; transform: translateY(-50%); font-size: 13px; color: var(--text-tertiary); font-weight: 600;">
                      <span *ngIf="servicechargeForm.get('type')?.value === 'percent'">%</span>
                      <span *ngIf="servicechargeForm.get('type')?.value === 'fixed'">&#8358;</span>
                    </span>
                  </div>
                </div>
                <div class="col-md-7">
                  <div class="btn-group">
                    <button type="button"
                            class="btn"
                            [class.btn-primary]="servicechargeForm.get('type')?.value === 'percent'"
                            [class.btn-outline-secondary]="servicechargeForm.get('type')?.value !== 'percent'"
                            (click)="servicechargeForm.patchValue({type: 'percent'})">
                      <i class="fas fa-percentage me-1"></i>Percentage
                    </button>
                    <button type="button"
                            class="btn"
                            [class.btn-primary]="servicechargeForm.get('type')?.value === 'fixed'"
                            [class.btn-outline-secondary]="servicechargeForm.get('type')?.value !== 'fixed'"
                            (click)="servicechargeForm.patchValue({type: 'fixed'})">
                      <i class="fas fa-coins me-1"></i>Fixed Amount
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        }

        <div style="padding-top: 20px; border-top: 1px solid var(--border); margin-top: 20px; display: flex; justify-content: flex-end;">
          <button class="btn btn-primary"
                  (click)="submit()"
                  [disabled]="isSubmitting || vatForm.invalid || taxForm.invalid || servicechargeForm.invalid || isLoadingDeductions">
            <span *ngIf="!isSubmitting">
              <i class="fas fa-save me-1"></i>Save Deductions
            </span>
            <span *ngIf="isSubmitting">
              <i class="fas fa-spinner fa-spin me-1"></i>Saving...
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Sidebar -->
  <div class="col-lg-4">
    <!-- Configuration Preview -->
    <div class="data-panel" style="margin-bottom: 20px;">
      <div class="panel-header">
        <div class="panel-title">
          <div class="title-icon">
            <i class="fas fa-eye"></i>
          </div>
          <div class="title-content">
            <div class="title-text">Configuration Preview</div>
            <div class="title-subtitle">Review your current settings</div>
          </div>
        </div>
      </div>

      <div class="panel-body" style="padding: 20px;">
        <!-- Business Info -->
        <div style="margin-bottom: 20px;">
          <div style="font-size: 12px; font-weight: 600; color: var(--text-tertiary); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 12px;">
            <i class="fas fa-store me-1"></i>Business Info
          </div>
          <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid var(--border-subtle);">
            <span style="font-size: 13px; color: var(--text-secondary);">Name:</span>
            <span style="font-size: 13px; font-weight: 600; color: var(--text-primary);">{{ name || 'Not set' }}</span>
          </div>
          <div style="display: flex; justify-content: space-between; padding: 8px 0;">
            <span style="font-size: 13px; color: var(--text-secondary);">Revenue Head:</span>
            <span style="font-size: 13px; font-weight: 600; color: var(--text-primary);">{{ revenue_head || 'Not set' }}</span>
          </div>
        </div>

        <!-- Deductions -->
        <div>
          <div style="font-size: 12px; font-weight: 600; color: var(--text-tertiary); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 12px;">
            <i class="fas fa-calculator me-1"></i>Deductions
          </div>
          <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid var(--border-subtle);">
            <span style="font-size: 13px; color: var(--text-secondary);">VAT:</span>
            <span style="font-size: 13px; font-weight: 600; color: var(--text-primary);">
              {{ vatForm.get('value')?.value || 0 }}{{ vatForm.get('type')?.value === 'percent' ? '%' : ' &#8358;' }}
            </span>
          </div>
          <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid var(--border-subtle);">
            <span style="font-size: 13px; color: var(--text-secondary);">Withholding Tax:</span>
            <span style="font-size: 13px; font-weight: 600; color: var(--text-primary);">
              {{ taxForm.get('value')?.value || 0 }}{{ taxForm.get('type')?.value === 'percent' ? '%' : ' &#8358;' }}
            </span>
          </div>
          <div style="display: flex; justify-content: space-between; padding: 8px 0;">
            <span style="font-size: 13px; color: var(--text-secondary);">Service Charge:</span>
            <span style="font-size: 13px; font-weight: 600; color: var(--text-primary);">
              {{ servicechargeForm.get('value')?.value || 0 }}{{ servicechargeForm.get('type')?.value === 'percent' ? '%' : ' &#8358;' }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Help Panel -->
    <div class="data-panel">
      <div class="panel-header">
        <div class="panel-title">
          <div class="title-icon">
            <i class="fas fa-question-circle"></i>
          </div>
          <div class="title-content">
            <div class="title-text">Configuration Help</div>
            <div class="title-subtitle">Tips and guidelines</div>
          </div>
        </div>
      </div>

      <div class="panel-body" style="padding: 20px;">
        <div style="display: flex; gap: 12px; margin-bottom: 16px; padding-bottom: 16px; border-bottom: 1px solid var(--border-subtle);">
          <div style="width: 32px; height: 32px; border-radius: var(--radius-sm); background: var(--warning-bg); color: var(--warning-text); display: flex; align-items: center; justify-content: center; font-size: 13px; flex-shrink: 0;">
            <i class="fas fa-key"></i>
          </div>
          <div>
            <div style="font-size: 13px; font-weight: 600; color: var(--text-primary); margin-bottom: 4px;">API Keys</div>
            <div style="font-size: 12px; color: var(--text-tertiary); line-height: 1.5;">Keep your secret key secure and never share it publicly. Use the public key for client-side integrations.</div>
          </div>
        </div>

        <div style="display: flex; gap: 12px; margin-bottom: 16px; padding-bottom: 16px; border-bottom: 1px solid var(--border-subtle);">
          <div style="width: 32px; height: 32px; border-radius: var(--radius-sm); background: var(--info-bg); color: var(--info-text); display: flex; align-items: center; justify-content: center; font-size: 13px; flex-shrink: 0;">
            <i class="fas fa-percentage"></i>
          </div>
          <div>
            <div style="font-size: 13px; font-weight: 600; color: var(--text-primary); margin-bottom: 4px;">Tax Configuration</div>
            <div style="font-size: 12px; color: var(--text-tertiary); line-height: 1.5;">Set percentage rates for flexible calculations or fixed amounts for consistent charges across all transactions.</div>
          </div>
        </div>

        <div style="display: flex; gap: 12px;">
          <div style="width: 32px; height: 32px; border-radius: var(--radius-sm); background: var(--accent-subtle); color: var(--accent); display: flex; align-items: center; justify-content: center; font-size: 13px; flex-shrink: 0;">
            <i class="fas fa-image"></i>
          </div>
          <div>
            <div style="font-size: 13px; font-weight: 600; color: var(--text-primary); margin-bottom: 4px;">Logo Guidelines</div>
            <div style="font-size: 12px; color: var(--text-tertiary); line-height: 1.5;">Upload a clear, high-resolution logo in PNG or JPG format. Recommended size is 200x200 pixels.</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
